import { Link } from 'react-router-dom';
import { CheckCircle, Star } from 'lucide-react';

export const PricingPage = () => {
  const packages = [
    {
      name: 'Starter',
      tagline: 'Perfect for getting started',
      price: 'Starting at $1,500',
      description: 'Ideal for startups and small businesses looking to establish their online presence.',
      timeline: '3-7 days',
      features: [
        'Single landing page',
        'Mobile-responsive design',
        'Contact form integration',
        'Basic SEO setup',
        'Google Analytics',
        '1 round of revisions',
        '30 days of support',
      ],
      notIncluded: ['CMS integration', 'E-commerce functionality', 'Custom features'],
      popular: false,
    },
    {
      name: 'Growth',
      tagline: 'Most popular choice',
      price: 'Starting at $3,500',
      description: 'Complete business website with multiple pages and content management.',
      timeline: '7-14 days',
      features: [
        'Up to 7 pages',
        'Custom design system',
        'CMS integration (WordPress/Webflow)',
        'Advanced SEO optimization',
        'Contact & newsletter forms',
        'Blog setup',
        '2 rounds of revisions',
        '60 days of support',
      ],
      notIncluded: ['E-commerce', 'Custom web apps'],
      popular: true,
    },
    {
      name: 'Pro',
      tagline: 'For ambitious projects',
      price: 'Starting at $7,500',
      description: 'Full-featured e-commerce or advanced website with custom functionality.',
      timeline: '14-21 days',
      features: [
        'Unlimited pages',
        'E-commerce setup (Shopify/WooCommerce)',
        'Custom feature development',
        'Advanced integrations',
        'Performance optimization',
        'Comprehensive SEO',
        '3 rounds of revisions',
        '90 days of support',
        'Priority support',
      ],
      notIncluded: [],
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6" data-testid="pricing-hero-heading">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto" data-testid="pricing-hero-description">
            Choose the package that fits your needs. All prices are starting points — final pricing depends on your specific requirements.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 md:py-32 bg-white" data-testid="pricing-cards-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`bg-white border rounded-3xl p-8 hover:border-[#10B981] transition-colors duration-300 flex flex-col h-full ${
                  pkg.popular ? 'border-[#10B981] border-2 shadow-lg relative' : 'border-slate-200'
                }`}
                data-testid={`pricing-card-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2" data-testid={`pricing-badge-${index}`}>
                    <span className="bg-[#10B981] text-white px-6 py-2 rounded-full font-bold text-sm inline-flex items-center gap-2">
                      <Star size={16} fill="white" /> Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid={`pricing-name-${index}`}>
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4" data-testid={`pricing-tagline-${index}`}>
                    {pkg.tagline}
                  </p>
                  <div className="text-3xl font-bold text-slate-900 mb-2" data-testid={`pricing-price-${index}`}>
                    {pkg.price}
                  </div>
                  <div className="text-sm text-slate-600 mb-4" data-testid={`pricing-timeline-${index}`}>
                    Typical timeline: {pkg.timeline}
                  </div>
                  <p className="text-slate-600" data-testid={`pricing-description-${index}`}>
                    {pkg.description}
                  </p>
                </div>

                <div className="flex-1">
                  <h4 className="font-heading font-bold text-slate-900 mb-4">What's included:</h4>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2" data-testid={`pricing-feature-${index}-${i}`}>
                        <CheckCircle size={20} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {pkg.notIncluded.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-heading font-bold text-slate-900 mb-3 text-sm">Not included:</h4>
                      <ul className="space-y-2">
                        {pkg.notIncluded.map((item, i) => (
                          <li key={i} className="text-sm text-slate-500" data-testid={`pricing-not-included-${index}-${i}`}>
                            • {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <Link
                  to="/contact"
                  className={`block text-center rounded-full px-8 py-4 font-bold transition-all duration-300 hover:-translate-y-0.5 ${
                    pkg.popular
                      ? 'bg-[#10B981] text-white hover:bg-[#059669] shadow-lg shadow-green-500/20'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4" data-testid="pricing-disclaimer">
              <strong>Note:</strong> Final pricing depends on project scope, complexity, and specific requirements. All packages can be customized to fit your needs.
            </p>
            <p className="text-slate-600" data-testid="pricing-contact-note">
              Need a custom solution? <Link to="/contact" className="text-[#10B981] hover:text-[#059669] font-medium">Get in touch</Link> for a personalized quote.
            </p>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="pricing-addons-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="addons-heading">
              Popular Add-ons
            </h2>
            <p className="text-lg text-slate-600" data-testid="addons-description">
              Enhance any package with these additional services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Logo Design', price: 'From $500' },
              { name: 'Content Writing', price: 'From $200/page' },
              { name: 'Professional Photography', price: 'From $800' },
              { name: 'Custom Illustrations', price: 'From $300' },
              { name: 'Video Production', price: 'From $1,500' },
              { name: 'Email Marketing Setup', price: 'From $400' },
              { name: 'Social Media Integration', price: 'From $300' },
              { name: 'Advanced Analytics', price: 'From $500' },
            ].map((addon, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-xl p-6 text-center hover:border-[#10B981] transition-colors duration-300"
                data-testid={`addon-${index}`}
              >
                <h3 className="font-heading font-bold text-slate-900 mb-2" data-testid={`addon-name-${index}`}>
                  {addon.name}
                </h3>
                <p className="text-[#10B981] font-bold" data-testid={`addon-price-${index}`}>
                  {addon.price}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 md:py-32 bg-white" data-testid="pricing-faq-teaser">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-testid="faq-teaser-heading">
            Have questions about pricing?
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-testid="faq-teaser-description">
            Check our FAQ or get in touch for a personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#faq"
              className="bg-white text-slate-900 border border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-full px-8 py-4 font-medium transition-all duration-300"
              data-testid="faq-teaser-faq-button"
            >
              View FAQ
            </Link>
            <Link
              to="/contact"
              className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full px-8 py-4 font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all duration-300 hover:-translate-y-1"
              data-testid="faq-teaser-contact-button"
            >
              Request Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
